import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import type { ForumLang } from "../constants/forumTranslations";

const LANG_STORAGE_KEY = "forumLang";
const BRAND_NAVY_LIGHT = "#2a2f55";

const RefundPolicy: React.FC = () => {
  const [lang, setLang] = useState<ForumLang>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === "es" || stored === "en") return stored;
    } catch {
      // ignore
    }
    return "es";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      // ignore
    }
  }, [lang]);

  const isEs = lang === "es";

  return (
    <div className="min-h-screen bg-[#f8fafc] flex flex-col overflow-x-hidden">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 h-24 flex items-center justify-between">
          <div className="flex items-center space-x-5">
            <Link to="/" className="flex items-center space-x-5">
              <div className="w-16 h-16 flex items-center justify-center overflow-hidden">
                <img
                  src="/logo/logo.png"
                  alt="American School"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="border-l border-gray-100 pl-5 py-1">
                <h1
                  className="text-xl font-black tracking-tight uppercase leading-none"
                  style={{ color: "#161b3d" }}
                >
                  American School
                </h1>
                <p
                  className="text-xs font-bold mt-1 tracking-wider uppercase"
                  style={{ color: "#8b1e22" }}
                >
                  {isEs ? "Política de Reembolsos y Cancelaciones" : "Refund and Cancellation Policy"}
                </p>
              </div>
            </Link>
          </div>

          <div className="flex rounded border border-gray-200/80 bg-gray-50/60 p-px" role="group" aria-label={isEs ? "Idioma" : "Language"}>
            <button
              type="button"
              onClick={() => setLang("es")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "es"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "es" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              ES
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider transition-colors ${
                lang === "en"
                  ? "text-white"
                  : "text-gray-500 hover:bg-gray-100/80 hover:text-gray-700"
              }`}
              style={lang === "en" ? { backgroundColor: BRAND_NAVY_LIGHT } : undefined}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <motion.main
        className="flex-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <div className="max-w-3xl mx-auto px-4 py-10 md:py-16">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900 text-center mb-3 uppercase">
            {isEs ? "Política de Reembolsos y Cancelaciones" : "Refund and Cancellation Policy"}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 text-center max-w-2xl mx-auto uppercase tracking-wide mb-10">
            {isEs
              ? "La presente política establece las condiciones aplicables a pagos realizados a través de la plataforma de inscripción de American School."
              : "This policy sets out the conditions applicable to payments made through American School's enrollment platform."}
          </p>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-6 py-4">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 text-center">
                {isEs ? "Política de reembolsos y cancelaciones" : "Refund and Cancellation Policy"}
              </h2>
            </div>

            <ol className="divide-y divide-gray-100">
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">1. {isEs ? "Pagos realizados" : "Payments Made"}</p>
                <p>
                  {isEs
                    ? "Los pagos efectuados mediante esta plataforma corresponden a la inscripción del estudiante en clases o actividades ofrecidas por American School."
                    : "Payments made through this platform correspond to the student's enrollment in classes or activities offered by American School."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">2. {isEs ? "Confirmación de cupo" : "Spot Confirmation"}</p>
                <p>
                  {isEs
                    ? "Una vez realizado el pago y confirmada la inscripción, el cupo correspondiente en la actividad o clase seleccionada queda reservado para el estudiante."
                    : "Once payment has been made and enrollment confirmed, the corresponding spot in the selected activity or class is reserved for the student."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">3. {isEs ? "Reembolsos" : "Refunds"}</p>
                <p>
                  {isEs
                    ? "Los pagos realizados no son reembolsables una vez confirmada la inscripción, salvo en casos excepcionales determinados por American School."
                    : "Payments made are non-refundable once enrollment is confirmed, except in exceptional cases as determined by American School."}
                </p>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">4. {isEs ? "Cancelaciones por parte de la institución" : "Cancellation by the Institution"}</p>
                <p className="mb-2">
                  {isEs
                    ? "En caso de cancelación de una actividad por parte de American School, el usuario podrá optar por:"
                    : "If American School cancels an activity, the user may choose to:"}
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-800">
                  <li>{isEs ? "Transferir el pago a otra actividad o clase disponible." : "Transfer the payment to another available activity or class."}</li>
                  <li>{isEs ? "Recibir un reembolso del monto pagado, según corresponda." : "Receive a refund of the amount paid, as applicable."}</li>
                </ul>
              </li>
              <li className="px-6 py-4 text-sm leading-relaxed text-gray-800">
                <p className="font-semibold mb-1">5. {isEs ? "Consultas" : "Inquiries"}</p>
                <p>
                  {isEs
                    ? "Para solicitudes relacionadas con pagos, cancelaciones o modificaciones en las inscripciones, los usuarios pueden comunicarse con nuestro equipo de soporte a través del correo electrónico:"
                    : "For requests related to payments, cancellations or changes to enrollments, users may contact our support team at the following email address:"}
                </p>
                <p className="mt-2">
                  <a href="mailto:info@americanschool.edu.do" className="text-[#5856d6] hover:text-[#3b3ab8] transition-colors">
                    info@americanschool.edu.do
                  </a>
                </p>
              </li>
            </ol>
          </div>
        </div>
      </motion.main>
    </div>
  );
};

export default RefundPolicy;
